import {
  Component,
  Inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FrontendChatComponent } from '@tablesready/frontend-chat';
import { FrontendKeyboardService } from '@tablesready/frontend-keyboard';
import {
  FloorPlanPartyType,
  FrontendPartyInfoCompactComponent,
  getPartyType,
} from '@tablesready/frontend-party-info-compact';
import {
  ChatCounterModule,
  FormValidationModule,
  NOT_ONLY_WHITESPACE_PATTERN,
  SMS_REPLY_PATTERN,
  SmsWarningModule,
} from '@tablesready/common-ui';
import { Location } from '@shared/location/models';
import { Inbound, Notification, Party } from '@shared/party';
import { Angulartics2Module } from 'angulartics2';
import { FrontendDropdownModule } from '@tablesready/frontend-dropdown';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AppIcon } from '@tablesready/frontend-icons';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import {
  debounceTime,
  tap,
  merge,
  filter,
  Subscription,
  switchMap,
  catchError,
  of,
  take,
} from 'rxjs';
import { ReplyService } from '../reply.service';
import { PartiesService } from '@tablesready/frontend-api-services';
import { MessageSenderService } from '../message-sender.service';
import { Reply } from '@shared/reply/models';
import { SocketService } from '@tablesready/frontend-store';
import { WebSocketEventsEnum } from '@shared/websocket';
import { BadgeModule } from '@tablesready/frontend-badge';

@Component({
  selector: 'lib-frontend-chat-party',
  standalone: true,
  imports: [
    CommonModule,
    FrontendChatComponent,
    FrontendPartyInfoCompactComponent,
    Angulartics2Module,
    ChatCounterModule,
    FormValidationModule,
    FrontendDropdownModule,
    ReactiveFormsModule,
    SmsWarningModule,
    BadgeModule,
  ],
  templateUrl: './frontend-chat-party.component.html',
  styleUrl: './frontend-chat-party.component.scss',
})
export class FrontendChatPartyComponent implements OnInit, OnDestroy, OnChanges {
  @Input() party?: Party | null;
  @Input() cancel?: () => void;
  showPartyInfo = input(false);
  timezone = input<string>('');
  editPartyMethod = input<(party: Party) => void>();
  location = input<Location>();
  showReplyForm = input(true);
  showTemplates = input(false);
  /** False on plans without 1:1 chat: compose still renders, sending upsells instead. */
  canSendChat = input(true);
  onUpgradeNeeded = input<() => void>();
  hideTitle = input(false);

  form?: UntypedFormGroup;
  icon = AppIcon;
  smsReply = '';
  messages: Array<Notification | Inbound> = [];
  partyType = FloorPlanPartyType.booked;
  maxLength: number;
  templates: Reply[] = [];
  replyTemplate: string | null = null; // Added this to display placeholder on ng-select with [multiple]=false option
  dropDownOpened = false;
  readonly maxAnsiLength: number;
  readonly maxNonAnsiLength: number;
  private readonly debounceMs = 400;
  private readonly subscriptions = new Subscription();
  private readonly chatRef = viewChild(FrontendChatComponent);

  constructor(
    private readonly partiesService: PartiesService,
    private readonly messageSenderService: MessageSenderService,
    private readonly socketsService: SocketService,
    @Inject(APP_VARIABLES_TOKEN) readonly appVariables: AppVariables,
    private readonly replyService: ReplyService,
    private readonly formBuilder: FormBuilder,
    private readonly keyboardService: FrontendKeyboardService,
  ) {
    this.maxAnsiLength = Number(this.appVariables.WHITELABEL_SMS_CHARS);
    this.maxNonAnsiLength = Number(this.appVariables.WHITELABEL_SMS_NON_ANSI_CHARS);
    this.maxLength = this.maxAnsiLength;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['party'] && this.party) {
      this.partyType = getPartyType(this.party);

      setTimeout(() => {
        this.mergeInboundOutbound();
        // Wait one more tick so the timeline *ngIf renders before scrolling.
        setTimeout(() => this.chatRef()?.scrollToBottom('auto'), 0);
      });
    }
  }

  ngOnInit(): void {
    const eventsSubscription = merge(
      this.socketsService.getEventStream(WebSocketEventsEnum.parties),
      this.socketsService.getEventStream(WebSocketEventsEnum.TextSmsEvent),
      this.socketsService.getEventStream(WebSocketEventsEnum.VoiceSmsEvent),
      this.socketsService.getEventStream(WebSocketEventsEnum.InboundSmsEvent),
    )
      .pipe(
        debounceTime(this.debounceMs),
        tap(() => {
          this.partiesEventHandler();
        }),
      )
      .subscribe();
    this.subscriptions.add(eventsSubscription);

    const keyboardSub = this.keyboardService.keyboardVisible$
      .pipe(filter((visible) => visible))
      .subscribe(() => {
        // Keyboard animation + WebView resize on iOS settles ~300ms after
        // keyboardDidShow. Re-scroll across that window so the latest message
        // ends up at the bottom regardless of when the layout actually lands.
        [0, 150, 350].forEach((delay) => {
          setTimeout(() => this.chatRef()?.scrollToBottom('auto'), delay);
        });
      });
    this.subscriptions.add(keyboardSub);

    if (this.showTemplates()) {
      this.subscriptions.add(this.replyService.listenToChanges().subscribe());
      const repliesSubscription = this.replyService
        .load()
        .pipe(
          switchMap(() => this.replyService.selectAll()),
          tap((replies) => {
            this.templates = replies;
          }),
        )
        .subscribe();
      this.subscriptions.add(repliesSubscription);
    }

    this.form = this.formBuilder.group({
      smsReply: [
        null,
        [
          Validators.required,
          Validators.maxLength(this.maxLength),
          Validators.pattern(SMS_REPLY_PATTERN),
          Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN),
        ],
      ],
      replyTemplate: [null],
    });

    const replyTemplateSub = this.form
      .get('replyTemplate')
      .valueChanges.pipe(
        filter((id) => !!id),
        tap((id) => {
          const template = this.templates.find((template) => template.id === id);
          if (template) {
            this.smsReplyControl.setValue(template.text);
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(replyTemplateSub);

    this.mergeInboundOutbound();
  }

  mergeInboundOutbound(): void {
    const messages = [];
    if (this.party?.notifications) {
      messages.push(...this.party.notifications);
    }
    if (this.party?.inbounds) {
      messages.push(...this.party.inbounds);
    }

    this.messages = messages
      //.filter((message) => {
      //  return (message as any).status_sent !== 'not_sent';
      //})
      .sort((a, b) => a.time - b.time);
  }

  partiesEventHandler(): void {
    if (!this.party) {
      return;
    }

    this.partiesService
      .getById(this.party._id)
      .pipe(
        tap((party) => {
          this.party = party;
          this.mergeInboundOutbound();
        }),
        catchError((err) => {
          this.party = null;

          return of(err);
        }),
        take(1),
      )
      .subscribe();
  }

  savedReplyOptionText = (item: Reply): string => {
    return `<p class="m-0 line-zero-seven">
      <span class="font-weight-bold">${item.name}</span>
      <br><span class="text-wrap">${item.text}</span>
      </p>`;
  };

  savedReplyText = (item: Reply): string => {
    return item.name;
  };

  sendSMS(): void {
    if (!this.canSendChat()) {
      this.onUpgradeNeeded()?.();

      return;
    }
    if (!this.party?.phone) {
      return; // no phone → cannot send (mirrors the disabled chat buttons elsewhere)
    }
    this.messageSenderService.smsPage(
      this.party,
      this.smsReplyControl.value.trim(),
      'chat',
    );
    this.form?.reset();
  }

  onDropdownTriggered(opened: boolean): void {
    this.dropDownOpened = opened;
  }

  get smsReplyControl(): AbstractControl {
    return this.form.get('smsReply');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
